import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";

import api from "../../utils/api";
import config from "../../config/config";
import { useHubSnackBar } from "../useSnackBar";
import { invalidate } from "../../helpers/clientHelper";

const ATTACHMENTS = "/attachments";

// ── Queries ──

const getAttachments = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1] || {});
  return api.get(`${ATTACHMENTS}?${queryString}`);
};

export const useGetAttachments = ({ resourceType, resourceId, category }) =>
  useQuery(
    [
      ATTACHMENTS,
      {
        resourceType,
        resourceId,
        ...(category != null ? { category } : {}),
      },
    ],
    getAttachments,
    { enabled: !!resourceType && !!resourceId },
  );

// ── Mutations ──

// multipart/form-data upload — payload accepts:
//   { file, resourceType, resourceId, category,
//     customerId?, partId?, vendorId?, stationId?, price? }
// Optional metadata fields are skipped if null/undefined.
const META_FIELDS = ["customerId", "partId", "vendorId", "stationId", "price"];

const postAttachment = ({
  file,
  resourceType,
  resourceId,
  category,
  ...meta
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("resourceType", resourceType);
  formData.append("resourceId", String(resourceId));
  formData.append("category", String(category));
  META_FIELDS.forEach((k) => {
    if (meta[k] != null && meta[k] !== "") {
      formData.append(k, String(meta[k]));
    }
  });
  // Don't pass headers — the api interceptor already detects FormData and
  // skips its default `Content-Type: application/json` so the browser can
  // emit `multipart/form-data; boundary=…` itself.
  return api.post(ATTACHMENTS, formData);
};

export const usePostAttachment = () => {
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postAttachment, {
    onSuccess: () => {
      invalidate(queryClient, ATTACHMENTS);
      addSnackbarSuccess({ message: "File uploaded" });
    },
    onError: (error) => addSnackbarError({ error }),
  });
};

const deleteAttachment = (id) => api.delete(`${ATTACHMENTS}/${id}`);

export const useDeleteAttachment = () => {
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteAttachment, {
    onSuccess: () => {
      invalidate(queryClient, ATTACHMENTS);
      addSnackbarSuccess({ message: "File deleted" });
    },
    onError: (error) => addSnackbarError({ error }),
  });
};

// Build a server URL the browser can hit directly (download or inline preview).
// Cookies travel automatically since the API uses `withCredentials`.
//   inline=true → opens in browser (PDF/image preview)
//   inline=false (default) → triggers download
export const getAttachmentDownloadUrl = (id, { inline = false } = {}) =>
  `${config.apiHost}${ATTACHMENTS}/${id}/download${
    inline ? "?inline=true" : ""
  }`;
